import * as zod from 'zod';

const HostCommandName = zod.enum([
  'echoTest',
  'services_restart',
  'configureModbusRTUUart',
  'installFirmware',
  'factoryReset',
  'toggleDHCPServer',
  'checkCPU',
  'mountUSB',
  'umountUSB',
  'rescanUSB',
  'listDrivesUSB',
  'codeBlocks',
  'controlBoxTemp',
  'botctlUtils',
  'startExternalControlBot',
  'stopExternalControlBot',
  'startSkillsBotInference',
  'stopSkillsBotInference',
  'updateEtherNetIPAddress',
  'networkConfigure',
  'networkStatus',
  'certificatesResolve',
  'writeCalibrationFlash',
]);

const EchoTestCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.echoTest),
  output: zod.string().optional(),
});

const ServiceRestart = zod.object({
  command: zod.literal(HostCommandName.Enum.services_restart),
  service: zod
    .enum([
      'simulator-bot',
      'metrics-bot',
      'can-proxy-bot',
      'postgres-bot',
      'ngrok-bot',
      'updater-bot',
      'camera-bot',
      'camera-bot-cpp',
      'teleop-bot',
      'ros-proxy-bot',
      'feathers-bot',
      'remote-control',
      'motion-planner-bot',
      'arm-control-bot',
      'botman',
      'agent-bot',
      'vision-bot',
      'robby-server',
      'kalibrate-bot',
      'assembly-script-bot',
      'service-hub-bot',
      'skills-bot-inference',
      'ros2-bridge-bot',
      'ros2-control-bot',
      'compliance-control-bot',
      'echo-bot',
      'external-control-bot',
      'media-bot',
      'mujoco-bot',
      'all',
    ])
    .default('all'),
  output: zod.string().optional(),
});

export const ModbusRTUUartConfig = zod.object({
  interfaceId: zod.number().optional(),
  baudRate: zod.number(),
  parity: zod.enum(['none', 'even', 'odd']),
  stopBits: zod.enum(['ZeroPointFive', 'One', 'OnePointFive', 'Two']),
  output: zod.string().optional(),
});

export type ModbusRTUUartConfig = zod.infer<typeof ModbusRTUUartConfig>;

const ConfigureModbusRTUUart = zod.object({
  command: zod.literal(HostCommandName.Enum.configureModbusRTUUart),
  configureModbusRTUUart: ModbusRTUUartConfig,
  output: zod.string().optional(),
});

// Restart or stop the DHCP server
const ToggleDHCPServer = zod.object({
  command: zod.literal(HostCommandName.Enum.toggleDHCPServer),
  enable: zod.boolean(),
  output: zod.string().optional(),
});

const InstallFirmwareCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.installFirmware),
  installFirmware: zod.object({
    joints: zod.array(zod.number()),
    force: zod.boolean().optional(),
    skipSoftwareShutdown: zod.boolean().optional(),
    build: zod.string().optional(),
    stlink: zod.boolean().optional(),
    firmwareType: zod.string().optional(),
    variant: zod.string().default('default').optional(),
    override: zod.boolean().optional(),
    retries: zod.number().optional(),
    interface: zod.string().optional(),
    // Build resolution, shared by the flash path and the --list read below:
    // treat `build` as a branch name and walk `minus` rungs back from its top.
    mostRecentBuild: zod.boolean().optional(),
    minus: zod.number().optional(),
    // Read mode: `--list [--json]` prints the resolved build's firmware catalog
    // (per firmwareType/variant checksums) and flashes nothing — used to compare
    // a candidate build against the active one without touching a board.
    list: zod.boolean().optional(),
    json: zod.boolean().optional(),
  }),
  output: zod.string().optional(),
});

const FactoryReset = zod.object({
  command: zod.literal(HostCommandName.Enum.factoryReset),
  output: zod.string().optional(),
});

const CheckCPUUsage = zod.object({
  command: zod.literal(HostCommandName.Enum.checkCPU),
  output: zod.string().optional(),
});

const MountUSB = zod.object({
  command: zod.literal(HostCommandName.Enum.mountUSB),
  name: zod.string(),
  // Optional filesystem type (from `lsblk` FSTYPE). When present the host
  // mounts with an explicit `-t <fstype>` and falls back to kernel autodetect;
  // when absent it autodetects with an exFAT/vFAT fallback chain.
  fstype: zod.string().optional(),
  output: zod.string().optional(),
});

const UmountUSB = zod.object({
  command: zod.literal(HostCommandName.Enum.umountUSB),
  name: zod.string(),
  output: zod.string().optional(),
});

// Force the kernel to re-enumerate USB card-reader SCSI hosts and re-probe
// media on already-known USB block devices. This is the programmatic
// equivalent of physically replugging the reader: it surfaces a card whose
// LUN was never enumerated (common with multi-slot readers) without any
// manual CLI step.
const RescanUSB = zod.object({
  command: zod.literal(HostCommandName.Enum.rescanUSB),
  output: zod.string().optional(),
});

const ListDrivesUSB = zod.object({
  command: zod.literal(HostCommandName.Enum.listDrivesUSB),
  output: zod.string().optional(),
});

const CodeBlocksCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.codeBlocks),
  codeBlockPath: zod.string(),
  routineId: zod.string(),
  robotToken: zod.string(),
  contextKind: zod.enum(['code-block-step', 'expression']),
  robotKind: zod.enum(['simulated', 'live']),
  output: zod.string().optional(),
});

const ControlBoxTempCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.controlBoxTemp),

  /**
   * Output is Pseudo-JSON.
   *
   * Why not JSON? No combination of `\\"`, `\\\\"`, `\"` survives the command processing/escaping. Always get back to something like this.
   *
   * If has CPU temps: (Then actual temp is 46 deg. celcius.)
   * {x86_pkg_temp:46000,acpitz:27800,iwlwifi_1:43000}
   *
   * If not: (E.g. if on VM)
   * {}
   */
  output: zod.string().optional(),
});

const BotctlUtilsCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.botctlUtils),
  type: zod.enum(['shutdown']),
  params: zod.string().optional(),
  output: zod.string().optional(),
});

const StartExternalControlBotCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.startExternalControlBot),
  output: zod.string().optional(),
});

const StopExternalControlBotCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.stopExternalControlBot),
  output: zod.string().optional(),
});

const StartSkillsBotInferenceCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.startSkillsBotInference),
  output: zod.string().optional(),
});

const StopSkillsBotInferenceCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.stopSkillsBotInference),
  output: zod.string().optional(),
});

const UpdateEtherNetIPAddressCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.updateEtherNetIPAddress),
  newHost: zod.string(),
  oldHost: zod.string().optional(),
  output: zod.string().optional(),
});

const NetworkConfigureCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.networkConfigure),
  args: zod.string(),
  output: zod.string().optional(),
});

const CertificatesResolveCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.certificatesResolve),
  interfaces: zod.string().regex(/^[a-z0-9][a-z0-9,-]*$/),
  output: zod.string().optional(),
});

const NetworkStatusCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.networkStatus),
  queryType: zod.enum([
    'ipLinkShow',
    'listInterfaces',
    'listAddresses',
    'checkHwTimestamp',
    'systemctlIsActive',
    'systemctlCat',
    'dnsmasqConfigExists',
    'cycloneddsConfig',
    'ptpStats',
    'dhcpLeases',
    'dhcpRange',
    'avahiConfig',
    'ufwStatus',
  ]),
  iface: zod
    .string()
    .regex(/^[a-z0-9][a-z0-9-]*$/)
    .optional(),
  output: zod.string().optional(),
});

const WriteCalibrationFlashCommand = zod.object({
  command: zod.literal(HostCommandName.Enum.writeCalibrationFlash),
  output: zod.string().optional(),
});

export const HostCommandData = zod.discriminatedUnion('command', [
  EchoTestCommand,
  InstallFirmwareCommand,
  FactoryReset,
  ServiceRestart,
  ConfigureModbusRTUUart,
  ToggleDHCPServer,
  CheckCPUUsage,
  MountUSB,
  UmountUSB,
  RescanUSB,
  ListDrivesUSB,
  CodeBlocksCommand,
  ControlBoxTempCommand,
  BotctlUtilsCommand,
  StartExternalControlBotCommand,
  StopExternalControlBotCommand,
  StartSkillsBotInferenceCommand,
  StopSkillsBotInferenceCommand,
  UpdateEtherNetIPAddressCommand,
  NetworkConfigureCommand,
  NetworkStatusCommand,
  CertificatesResolveCommand,
  WriteCalibrationFlashCommand,
]);

export type HostCommandName = zod.infer<typeof HostCommandName>;
export type HostCommandData = zod.infer<typeof HostCommandData>;

// Shared client types for running/stopping host commands
export type RunHostCommandOptions = {
  onStarted?: (e: { identifier: string; command: HostCommandName }) => void;
  onExited?: (e: { identifier: string; command: HostCommandName }) => void;
  timeout?: number; // 0 disables timeout
};

export type RunHostCommand = (
  command: HostCommandData,
  opts?: RunHostCommandOptions,
) => Promise<string | undefined>;

export type StopHostCommandResult = {
  identifier: string;
  stopped: boolean;
};

export type StopHostCommand = (
  identifier: string,
) => Promise<StopHostCommandResult>;

export type SignalHostCommandResult = {
  identifier: string;
  signaled: boolean;
};

// Notify a running host command that it should pause (e.g. invoke a user
// `on_pause` hook in a codeblock). Notification-only; the underlying
// process continues to run.
export type PauseHostCommand = (
  identifier: string,
) => Promise<SignalHostCommandResult>;

export type ResumeHostCommand = (
  identifier: string,
) => Promise<SignalHostCommandResult>;
